import React from 'react';
import type { RequirementWithDetails } from '../services/requirementService';

interface ProjectProgressCardProps {
  requirements: RequirementWithDetails[];
}

interface StatusCounts {
  draft: number;
  approved: number;
  'in-progress': number;
  completed: number;
  rejected: number;
  deferred: number;
}

export function ProjectProgressCard({ requirements }: ProjectProgressCardProps): React.ReactElement {
  // Calculate status counts
  const statusCounts: StatusCounts = requirements.reduce((acc, req) => {
    const status = req.status;
    if (status in acc) {
      acc[status as keyof StatusCounts] = (acc[status as keyof StatusCounts] || 0) + 1;
    }
    return acc;
  }, {
    draft: 0,
    approved: 0,
    'in-progress': 0,
    completed: 0,
    rejected: 0,
    deferred: 0
  } as StatusCounts);

  // Calculate totals (exclude rejected and deferred from active count)
  const totalActive = statusCounts.approved + statusCounts['in-progress'] + statusCounts.completed;
  const percentComplete = totalActive > 0 ? (statusCounts.completed / totalActive) * 100 : 0;

  // Total requirements (including all statuses)
  const totalRequirements = requirements.length;

  // Get status color
  const getStatusColor = (status: keyof StatusCounts): string => {
    const colors: Record<string, string> = {
      draft: 'bg-gray-500',
      approved: 'bg-yellow-500',
      'in-progress': 'bg-blue-500',
      completed: 'bg-green-500',
      rejected: 'bg-red-500',
      deferred: 'bg-purple-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  // Get status label
  const getStatusLabel = (status: keyof StatusCounts): string => {
    const labels: Record<string, string> = {
      draft: 'Draft',
      approved: 'Approved',
      'in-progress': 'In Progress',
      completed: 'Completed',
      rejected: 'Rejected',
      deferred: 'Deferred'
    };
    return labels[status] || status;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50 mb-4">
        Implementation Progress
      </h3>

      {/* Progress Bar */}
      {totalActive > 0 ? (
        <>
          <div className="relative pt-1">
            <div className="flex mb-2 items-center justify-between">
              <div>
                <span className="text-xs font-semibold inline-block text-indigo-600 dark:text-indigo-400">
                  {statusCounts.completed} of {totalActive} Active Requirements Completed
                </span>
              </div>
              <div className="text-right">
                <span className="text-xs font-semibold inline-block text-indigo-600 dark:text-indigo-400">
                  {percentComplete.toFixed(0)}%
                </span>
              </div>
            </div>
            <div className="overflow-hidden h-3 mb-4 text-xs flex rounded-full bg-gray-200">
              <div
                style={{ width: `${percentComplete}%` }}
                className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-gradient-to-r from-indigo-600 to-blue-600 transition-all duration-500"
              />
            </div>
          </div>

          {/* Status Breakdown - Main Active Statuses */}
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="text-center p-3 bg-green-50 rounded-lg border border-green-200">
              <div className="text-2xl font-bold text-green-700 dark:text-green-300">{statusCounts.completed}</div>
              <div className="text-xs text-green-600 font-medium">Completed</div>
            </div>
            <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="text-2xl font-bold text-blue-700 dark:text-blue-300">{statusCounts['in-progress']}</div>
              <div className="text-xs text-blue-600 font-medium">In Progress</div>
            </div>
            <div className="text-center p-3 bg-yellow-50 rounded-lg border border-yellow-200">
              <div className="text-2xl font-bold text-yellow-700 dark:text-yellow-300">{statusCounts.approved}</div>
              <div className="text-xs text-yellow-600 font-medium">Ready to Start</div>
            </div>
          </div>

          {/* All Status Badges */}
          <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-200">
            {(Object.keys(statusCounts) as Array<keyof StatusCounts>).map(status => {
              const count = statusCounts[status];
              if (count === 0) return null;

              return (
                <div
                  key={status}
                  className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-300"
                >
                  <div className={`w-2 h-2 rounded-full mr-2 ${getStatusColor(status)}`} />
                  <span>{getStatusLabel(status)}: {count}</span>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <div className="text-center py-8">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
            />
          </svg>
          <p className="mt-2 text-sm text-gray-500">
            {totalRequirements === 0
              ? 'No requirements yet'
              : 'No active requirements to track'}
          </p>
        </div>
      )}
    </div>
  );
}
